import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { Typography } from '../typography';
import type { TranslateAgnosticPriceProps } from './types';
import { getPriceFormatter, useResponsivePriceSize } from './utils';

const modelsWithPrefix = ['from'];
const modelsWithPostfix = ['subscription', 'per'];

const defaultFontValue = {
  fontWeight: 'bold',
  fontSize: 15
} as const;
const defaultColor = 'rgb(17, 17, 17)';

export const TranslateAgnosticPrice = memo(
  ({
    freeLabel,
    startingAtLabel,
    postfix,
    className,
    style,
    pricingModel,
    currency = 'USD',
    price,
    customPrice,
    minPrice,
    maxPrice,
    language,
    minFontSize = 1,
    valueFont = defaultFontValue,
    valueColor = defaultColor,
    affixFont = valueFont,
    affixColor = valueColor,
    isVertical = false,
    strikethrough = false,
    thousandsSeparator,
    decimalSeparator,
    decimalPlaces,
    ...forwardingProps
  }: TranslateAgnosticPriceProps) => {
    const { language: locale = 'en' } = language ?? {};

    const formattedPrice = useMemo(() => {
      const formatPrice = getPriceFormatter({
        locale,
        currency,
        thousandsSeparator,
        decimalSeparator,
        decimalPlaces
      });

      if (pricingModel === 'range') {
        const formattedMinPrice = formatPrice(minPrice);
        const formattedMaxPrice = formatPrice(maxPrice);

        return `${formattedMinPrice} - ${formattedMaxPrice}`;
      }

      if (pricingModel === 'free') {
        return freeLabel;
      }

      if (pricingModel === 'custom') {
        return customPrice;
      }

      return formatPrice(price);
    }, [
      locale,
      freeLabel,
      currency,
      pricingModel,
      minPrice,
      maxPrice,
      customPrice,
      price,
      thousandsSeparator,
      decimalSeparator,
      decimalPlaces
    ]);

    const hasPrefix = modelsWithPrefix.includes(pricingModel);
    const hasPostfix = modelsWithPostfix.includes(pricingModel);

    const shouldBeVertical = isVertical && pricingModel === 'from';
    const shouldStrikethrough = strikethrough && pricingModel !== 'free';

    const hasNoValue = pricingModel === 'custom' && !customPrice;

    const { fontSize: valueFontSize } = valueFont;
    const { fontSize: affixFontSize } = affixFont;
    const { containerRef, valueRef, affixRef } = useResponsivePriceSize({
      initialValueFont: valueFontSize,
      initialAffixFont: affixFontSize,
      isVertical: shouldBeVertical,
      minFontSize,
      pricingModel
    });

    if (hasNoValue) {
      return null;
    }

    return (
      <Wrapper
        {...forwardingProps}
        ref={containerRef}
        style={style}
        className={className}
        _shouldStrikethrough={shouldStrikethrough}
        _isVertical={shouldBeVertical}
      >
        {hasPrefix && (
          <Prefix
            ref={affixRef}
            style={{ fontSize: 0 }}
            textStyle={affixFont}
            color={affixColor}
            _isVertical={shouldBeVertical}
          >
            {startingAtLabel}
          </Prefix>
        )}

        <Value
          ref={valueRef}
          style={{ fontSize: 0 }}
          textStyle={valueFont}
          color={valueColor}
          _strikethrough={shouldStrikethrough}
          _isVertical={shouldBeVertical}
        >
          {formattedPrice}
        </Value>

        {hasPostfix && (
          <Postfix
            ref={affixRef}
            style={{ fontSize: 0 }}
            textStyle={affixFont}
            color={affixColor}
          >
            / {postfix}
          </Postfix>
        )}
      </Wrapper>
    );
  }
);

const Wrapper = styled.div.attrs<{
  _isVertical?: boolean;
  _shouldStrikethrough?: boolean;
}>(({ _shouldStrikethrough }) => ({
  className: createReadableClass({
    component: 'price',
    element: 'wrapper',
    index: _shouldStrikethrough && 'strikethrough'
  })
}))`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  white-space: nowrap;
  align-items: baseline;
  gap: 4px;
  flex-direction: ${({ _isVertical }) => (_isVertical ? 'column' : 'row')};
`;

const Prefix = styled(Typography).attrs<{
  _isVertical?: boolean;
}>({
  className: createReadableClass({
    component: 'price',
    element: 'prefix'
  })
})`
  ${({ _isVertical }) => _isVertical && 'width: 100%;'}
`;

const Value = styled(Typography).attrs<{
  _isVertical?: boolean;
  _strikethrough?: boolean;
}>({
  className: createReadableClass({
    component: 'price',
    element: 'value'
  })
})`
  ${({ _isVertical }) => _isVertical && 'width: 100%;'}
  ${({ _strikethrough }) => _strikethrough && 'text-decoration: line-through;'}
`;

const Postfix = styled(Typography).attrs({
  className: createReadableClass({
    component: 'price',
    element: 'postfix'
  })
})``;
